import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsArticle } from "@/mocks/news";
import { formatDate, getCategoryById } from "@/utils/news";

interface Props {
  slides: NewsArticle[];
}

export default function HeroCarousel({ slides }: Props) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const prev = () => setIndex((current) => (current - 1 + slides.length) % slides.length);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const timer = window.setInterval(next, 5600);
    return () => window.clearInterval(timer);
  }, [next, paused, slides.length]);

  if (slides.length === 0) return null;

  return (
    <section
      className="relative w-full h-[320px] sm:h-[380px] md:h-[460px] rounded-lg overflow-hidden bg-background-800 group"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点资讯轮播"
    >
      {slides.map((slide, slideIndex) => {
        const category = getCategoryById(slide.categoryId);
        return (
          <Link
            key={slide.id}
            to={`/detail/${slide.id}`}
            className={`absolute inset-0 cursor-pointer transition-opacity duration-700 ${
              slideIndex === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
            }`}
            aria-hidden={slideIndex !== index}
          >
            <img
              src={slide.cover}
              alt={slide.title}
              title={`${slide.title} - 黑料吃瓜网`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/45 to-foreground-950/10" />
            <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6 md:p-8">
              <div className="flex items-center gap-2 mb-2 md:mb-3">
                <span className="px-2 py-0.5 rounded bg-accent-500 text-foreground-950 text-[11px] font-medium tracking-wide">
                  焦点
                </span>
                <span className="px-2 py-0.5 rounded bg-background-50/15 text-background-100 text-[11px] backdrop-blur-sm">
                  {category?.name}
                </span>
              </div>
              <h2 className="font-heading text-lg sm:text-2xl md:text-[32px] font-bold leading-snug text-background-50 line-clamp-2 max-w-3xl">
                {slide.title}
              </h2>
              <p className="hidden sm:block mt-2.5 text-[13px] md:text-sm text-background-200/90 leading-relaxed line-clamp-2 max-w-2xl">
                {slide.summary}
              </p>
              <div className="mt-3 flex items-center gap-3 text-[11px] md:text-xs text-background-300">
                <span>{slide.author}</span>
                <span className="text-background-400">·</span>
                <span>{formatDate(slide.publishedAt)}</span>
                <span className="hidden sm:inline-flex items-center gap-1 ml-1 text-accent-300">
                  阅读全文
                  <i className="ri-arrow-right-line" />
                </span>
              </div>
            </div>
          </Link>
        );
      })}

      <button
        type="button"
        onClick={prev}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-950/35 text-background-50 backdrop-blur-sm hover:bg-background-950/55 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={next}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-950/35 text-background-50 backdrop-blur-sm hover:bg-background-950/55 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute right-4 bottom-4 md:right-6 md:bottom-6 z-20 flex items-center gap-1.5">
        {slides.map((slide, dotIndex) => (
          <button
            key={slide.id}
            type="button"
            onClick={() => setIndex(dotIndex)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              dotIndex === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/60 hover:bg-background-50"
            }`}
            aria-label={`切换到第 ${dotIndex + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}